import React from "react";
import { cn } from "@/lib/utils";

export type ButtonNewVariant = "primary" | "danger" | "dark" | "neutral";

const VARIANT_CLASSES: Record<ButtonNewVariant, string> = {
	primary: "bg-brand text-brand-foreground hover:bg-brand/90",
	danger: "bg-danger text-white hover:bg-danger/90",
	// The repo's `--secondary` token is a light gray, so the dark action button
	// gets its own variant rather than borrowing a token that means something else
	dark: "bg-neutral-800 text-white hover:bg-neutral-700",
	neutral: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
};

interface BaseProps {
	children: React.ReactNode;
	variant?: ButtonNewVariant;
	className?: string;
	disabled?: boolean;
}

type ButtonNewProps = BaseProps &
	(
		| ({ href?: undefined } & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className">)
		| ({ href: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "className">)
	);

/**
 * Tailwind replacement for the styled-components {@link Button}.
 *
 * Renders an `<a>` when given `href`, otherwise a `<button>`. That matters for
 * rows of actions: wrapping a button in an anchor makes it a different flex
 * item from its siblings and it stops lining up, which is exactly what went
 * wrong in the invoice batch action bar. Fixed height (`h-10`) so every button
 * in a row matches regardless of element type.
 *
 * Customise via `className` (merged with `cn`) rather than the legacy
 * `margin`/`width`/`backgroundColor` style props.
 */
const ButtonNew = React.forwardRef<any, ButtonNewProps>(
	({ children, variant = "primary", className, href, ...props }, ref) => {
		const classes = cn(
			"inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 text-sm font-medium transition-colors",
			"focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400",
			"disabled:pointer-events-none disabled:opacity-50",
			VARIANT_CLASSES[variant],
			className
		);

		if (href !== undefined) {
			return (
				<a
					ref={ref}
					href={href}
					className={classes}
					{...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
				>
					{children}
				</a>
			);
		}

		return (
			<button
				ref={ref}
				type="button"
				className={classes}
				{...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
			>
				{children}
			</button>
		);
	}
);

ButtonNew.displayName = "ButtonNew";

export default ButtonNew;
